import { useCallback, useMemo, useState } from "react";
import { match } from "ts-pattern";

import { sameSettingValue } from "@thalamos/haven-common";
import type {
  SettingChange,
  SettingKey,
  SettingValueJson,
  SettingView,
} from "@thalamos/haven-common";

/** Unsaved changes on the page, at most one per setting. */
export type UnsavedSettings = ReadonlyMap<SettingKey, SettingChange>;

/** What a row shows: whether this holder states the setting, and what applies. */
export type ShownSetting = {
  stated: boolean;
  value: SettingValueJson;
};

/**
 * A setting as it would be with its unsaved change applied.
 *
 * A cleared setting shows what it would inherit from the layers above as last
 * read, so it is right only while nothing above changes before the save.
 */
export const shownSetting = (
  setting: SettingView,
  change: SettingChange | undefined,
): ShownSetting => {
  if (change === undefined) {
    return { stated: setting.stated !== null, value: setting.value };
  }

  return match(change)
    .with({ kind: "set" }, ({ value }) => ({ stated: true, value }))
    .with({ kind: "clear" }, () => ({
      stated: false,
      value: setting.inherited,
    }))
    .exhaustive();
};

/**
 * Whether a change would leave the setting as it is saved, so that putting a
 * value back removes the change rather than keeping one that does nothing.
 */
export const changesNothing = (
  setting: SettingView,
  change: SettingChange,
): boolean =>
  match(change)
    .with(
      { kind: "set" },
      ({ value }) =>
        setting.stated !== null && sameSettingValue(setting.stated, value),
    )
    .with({ kind: "clear" }, () => setting.stated === null)
    .exhaustive();

/** One holder's unsaved changes, until they are saved or discarded. */
export const useUnsavedSettings = () => {
  const [unsaved, setUnsaved] = useState<UnsavedSettings>(new Map());

  const changeSetting = useCallback(
    (setting: SettingView, change: SettingChange) => {
      setUnsaved((current) => {
        const next = new Map(current);

        if (changesNothing(setting, change)) {
          next.delete(setting.key);
        } else {
          next.set(setting.key, change);
        }

        return next;
      });
    },
    [],
  );

  const discard = useCallback(() => {
    setUnsaved(new Map());
  }, []);

  const changes = useMemo(() => [...unsaved.values()], [unsaved]);

  return { unsaved, changes, changeSetting, discard };
};
